/**
 * Redux thunks for course comments
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCourseComments,
  addComment as addCommentService,
  updateComment as updateCommentService,
  deleteComment as deleteCommentService,
} from '../../services/firestore/commentService';
import { CourseComment } from '@e-learning/shared/src/store/types';

/**
 * Fetch all comments for a course
 */
export const fetchCourseComments = createAsyncThunk(
  'comments/fetchCourseComments',
  async (courseId: string, { rejectWithValue }) => {
    try {
      const comments = await getCourseComments(courseId);
      return { courseId, comments };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch comments');
    }
  }
);

/**
 * Add a comment
 */
export const addComment = createAsyncThunk(
  'comments/addComment',
  async (
    {
      courseId,
      userId,
      userEmail,
      userName,
      comment,
    }: {
      courseId: string;
      userId: string;
      userEmail: string | null;
      userName: string | null;
      comment: string;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!comment.trim()) {
        throw new Error('Comment cannot be empty');
      }
      const newComment = await addCommentService({
        courseId,
        userId,
        userEmail,
        userName,
        comment: comment.trim(),
      });
      return { courseId, comment: newComment };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add comment');
    }
  }
);

/**
 * Update a comment
 */
export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({ commentId, commentText }: { commentId: string; commentText: string }, { rejectWithValue }) => {
    try {
      if (!commentText.trim()) {
        throw new Error('Comment cannot be empty');
      }
      const updatedComment = await updateCommentService(commentId, commentText.trim());
      return updatedComment;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update comment');
    }
  }
);

/**
 * Delete a comment
 */
export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async ({ courseId, commentId }: { courseId: string; commentId: string }, { rejectWithValue }) => {
    try {
      await deleteCommentService(commentId);
      return { courseId, commentId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete comment');
    }
  }
);

